/**
 * RecentlyDeletedSection — admin-only (canImpersonate) recovery list for
 * deleted campaign templates, rendered at the bottom of the Manage Templates
 * tab.
 *
 * Rows come from the crm_deleted_campaign_snapshots view: the latest
 * version snapshot of every template_id that no longer exists in
 * crm_campaign_templates (delete_campaign_template enriches that snapshot
 * with the subscriber list + deleted_at/deleted_by).
 *
 * Restore = restore_campaign_template RPC (full reinsert of template + nodes
 * with their ORIGINAL ids) + optional re-subscribe of everyone who was
 * subscribed at delete time via the subscribeToCampaign callable
 * (targetUserId per subscriber — the backend clones fresh nodes per user).
 * Subscriptions that were switched OFF at delete time are recreated disabled.
 * Company Automation runners are always recreated disabled by the backend;
 * re-enable from the Company Automations table.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Flex,
  Heading,
  HStack,
  Icon,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  fetchDeletedTemplateSnapshots,
  restoreTemplateVersion,
  bulkToggleUserCampaigns,
} from '../../../api/supabase/automations';

const fbFunctions = getFunctions(undefined, 'us-east1');

export default function RecentlyDeletedSection({ getUserName, onRestored }) {
  const toast = useToast();
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const subText = useColorModeValue('gray.500', 'gray.400');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // view row pending restore
  const [resubscribe, setResubscribe] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const cancelRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDeletedTemplateSnapshots();
      setRows(data || []);
    } catch (err) {
      console.error('[RecentlyDeletedSection] load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openConfirm = (row) => {
    setConfirmTarget(row);
    setResubscribe((row.subscriber_count || 0) > 0);
  };

  const handleRestore = async () => {
    if (!confirmTarget) return;
    const { template_id: templateId } = confirmTarget;
    const subscribers = Array.isArray(confirmTarget.snapshot?.subscribers)
      ? confirmTarget.snapshot.subscribers
      : [];
    setRestoring(true);
    try {
      // 1) Un-delete: reinsert the template + nodes exactly as snapshotted.
      await restoreTemplateVersion(templateId);

      // 2) Optionally rebuild the subscriptions that were destroyed with it.
      let resubscribed = 0;
      let failed = 0;
      if (resubscribe && subscribers.length) {
        const subscribeFn = httpsCallable(fbFunctions, 'subscribeToCampaign');
        const disabledCampaignIds = [];
        for (const sub of subscribers) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const res = await subscribeFn({ templateId, targetUserId: sub.user_id });
            resubscribed += 1;
            if (sub.is_enabled === false && res.data?.userCampaignId) {
              disabledCampaignIds.push(res.data.userCampaignId);
            }
          } catch (subErr) {
            // e.g. user no longer exists / already subscribed — keep going.
            failed += 1;
            console.warn('[RecentlyDeletedSection] re-subscribe failed for', sub.user_id, subErr.message);
          }
        }
        if (disabledCampaignIds.length) {
          try {
            await bulkToggleUserCampaigns(disabledCampaignIds, false);
          } catch (toggleErr) {
            console.warn('[RecentlyDeletedSection] could not restore disabled state:', toggleErr.message);
          }
        }
      }

      toast({
        title: 'Campaign restored',
        description:
          resubscribe && subscribers.length
            ? `"${confirmTarget.name}" is back${resubscribed ? ` with ${resubscribed} subscriber${resubscribed === 1 ? '' : 's'} re-subscribed` : ''}${failed ? ` (${failed} failed)` : ''}.`
            : `"${confirmTarget.name}" is back. Nobody was re-subscribed.`,
        status: failed ? 'warning' : 'success',
        duration: 6000,
        isClosable: true,
      });
      setConfirmTarget(null);
      await load();
      onRestored?.();
    } catch (err) {
      console.error('[RecentlyDeletedSection] restore error:', err);
      toast({ title: 'Restore failed', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Box>
      <Flex
        align="center"
        mb={isOpen ? 1 : 0}
        cursor="pointer"
        userSelect="none"
        onClick={() => setIsOpen(!isOpen)}
        py={1}
      >
        <HStack spacing={2}>
          <Icon as={isOpen ? FiChevronDown : FiChevronRight} boxSize={4} color="gray.500" />
          <Icon as={FiTrash2} boxSize={4} color="red.400" />
          <Heading size="sm">Recently Deleted</Heading>
          {loading ? (
            <Spinner size="xs" />
          ) : (
            <Badge colorScheme="gray" fontSize="xs" borderRadius="full" px={2}>
              {rows.length}
            </Badge>
          )}
        </HStack>
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <Text fontSize="xs" color={subText} mb={3}>
          Deleted campaign templates recoverable from their last snapshot. Restoring
          brings back the exact workflow and can re-subscribe everyone who had it.
        </Text>
        {loading ? (
          <Flex justify="center" py={6}><Spinner /></Flex>
        ) : !rows.length ? (
          <Text fontSize="sm" color={subText} py={4}>
            Nothing has been deleted recently.
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Deleted</Th>
                  <Th isNumeric>Steps</Th>
                  <Th isNumeric>Subscribers</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((r) => (
                  <Tr key={r.template_id} _hover={{ bg: hoverBg }}>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <HStack spacing={2}>
                          <Text fontWeight="medium" fontSize="sm">{r.name || 'Untitled'}</Text>
                          <Badge fontSize="2xs" variant="subtle" colorScheme={r.execution_scope === 'company' ? 'purple' : r.ownership_type === 'user' ? 'gray' : 'blue'}>
                            {r.execution_scope === 'company' ? 'Automation' : r.ownership_type === 'user' ? 'Personal' : 'Company'}
                          </Badge>
                          <Badge colorScheme="gray" fontSize="2xs" variant="subtle">v{r.version}</Badge>
                        </HStack>
                        {r.description && (
                          <Text fontSize="xs" color={subText} noOfLines={1} maxW="240px">{r.description}</Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="xs">{r.deleted_at ? new Date(r.deleted_at).toLocaleString() : '—'}</Text>
                        {r.deleted_by && getUserName && (
                          <Text fontSize="xs" color={subText}>by {getUserName(r.deleted_by)}</Text>
                        )}
                      </VStack>
                    </Td>
                    <Td isNumeric><Text fontSize="sm">{r.node_count ?? 0}</Text></Td>
                    <Td isNumeric><Text fontSize="sm">{r.subscriber_count ?? 0}</Text></Td>
                    <Td>
                      <Flex justify="flex-end">
                        <Button
                          leftIcon={<FiRotateCcw />}
                          size="xs"
                          colorScheme="green"
                          variant="outline"
                          onClick={() => openConfirm(r)}
                        >
                          Restore
                        </Button>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Collapse>

      <AlertDialog
        isOpen={!!confirmTarget}
        leastDestructiveRef={cancelRef}
        onClose={() => !restoring && setConfirmTarget(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Restore &ldquo;{confirmTarget?.name}&rdquo;?
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="start" spacing={3}>
                <Text fontSize="sm">
                  The campaign comes back exactly as it was when deleted
                  ({confirmTarget?.node_count ?? 0} step{(confirmTarget?.node_count ?? 0) === 1 ? '' : 's'}, v{confirmTarget?.version}).
                </Text>
                {(confirmTarget?.subscriber_count || 0) > 0 && (
                  <Checkbox
                    isChecked={resubscribe}
                    onChange={(e) => setResubscribe(e.target.checked)}
                    size="sm"
                  >
                    Re-subscribe the {confirmTarget.subscriber_count} previous
                    subscriber{confirmTarget.subscriber_count === 1 ? '' : 's'} (previously
                    disabled ones stay disabled)
                  </Checkbox>
                )}
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} size="sm" onClick={() => setConfirmTarget(null)} isDisabled={restoring}>
                Cancel
              </Button>
              <Button
                colorScheme="green"
                size="sm"
                ml={3}
                leftIcon={<FiRotateCcw />}
                onClick={handleRestore}
                isLoading={restoring}
              >
                Restore
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
